import { useCallback, useRef, useState, useSyncExternalStore } from 'react'
import { useTranslation } from 'react-i18next'
import { ZodError } from 'zod'
import type { CommandKey, PayloadOf } from '@gk/contracts'
import { useClub } from '../club/ClubProvider.tsx'
import type { EnqueueOptions } from '../sync/outbox.ts'
import { errorText } from './components.tsx'
import { onScreen, onScreenChange } from '../platform/visibility.ts'

/** Cosmetic write gate: the user's role (from the synced matrix) and the platform mode. The server decides, always. */
/** The width of an element in CSS pixels, followed as it changes (a ResizeObserver); null until measured, and in a browser without one or in a test.
 *  For a chart drawn in its own pixels (the timeline's lanes, the series): the SVG's viewBox takes this width, so a 13-px label is 13 px on a phone
 *  too, where a fixed 640-unit viewBox scaled by 0.49 drew 5-px ticks (M16a review, finding 1). The ref is a CALLBACK ref: the pages render a
 *  "loading" line before their chart, so the element mounts after the first render, where a layout effect with no dependencies would never see it
 *  (the audit harness caught the lanes still drawn at 640 units). */
export function useElementWidth<T extends HTMLElement>(): [(el: T | null) => void, number | null] {
  const [width, setWidth] = useState<number | null>(null)
  const stop = useRef<(() => void) | null>(null)
  const ref = useCallback((el: T | null) => {
    stop.current?.()
    stop.current = null
    if (!el) return
    const measure = () => { const w = Math.round(el.getBoundingClientRect().width); setWidth((old) => (w > 0 && w !== old ? w : old)) }
    measure()
    if (typeof ResizeObserver === 'undefined') return
    const ro = new ResizeObserver(measure)
    ro.observe(el)
    stop.current = () => ro.disconnect()
  }, [])
  return [ref, width]
}

export function useWriteAccess(permission: string): { allowed: boolean; permitted: boolean; readOnlyMode: boolean } {
  const { can, platform } = useClub()
  const permitted = can(permission)
  const readOnlyMode = platform.mode === 'online_only'
  return { allowed: permitted && !readOnlyMode, permitted, readOnlyMode }
}

/** The first value seen once `ready`, kept for the life of the component (null before). For a block whose place on a screen follows a fact the person
 *  changes on that very block (Today's weather card among the direction's exceptions, M23 item 30): moved the moment the fact changes, the block would be
 *  a new element at a new place, which React remounts, and what it showed ("Relevé enregistré") would be lost. The value is taken during the render that
 *  first sees it ready, so no render commits the block at the wrong place. */
export function useFirstReady<T>(value: T, ready: boolean): T | null {
  const [first, setFirst] = useState<T | null>(null)
  if (ready && first === null) { setFirst(value); return value }
  return first
}

/** Is the app on screen (platform/visibility.ts: the page's visibility and, in the native app, the app's own state)? What polls or takes a position
 *  stops at once when the phone is put away, also in the native app whose Android shell keeps its page "visible" in the background (M19b). */
export function usePageVisible(): boolean {
  return useSyncExternalStore(onScreenChange, onScreen, () => true)
}

/** Run a user action once at a time, turning a bad payload into a readable message (the same zod schema the server mirrors). */
export function useAction() {
  const { t } = useTranslation()
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const run = useCallback(async (fn: () => Promise<void>) => {
    setBusy(true); setError(null)
    try { await fn() } catch (e) {
      setError(e instanceof ZodError ? e.issues.map((i) => `${i.path.join('.')}: ${i.message}`).join(' · ') : e instanceof Error && e.message ? e.message : t('errors.unknown'))
    } finally { setBusy(false) }
  }, [t])
  return { busy, error, run, clearError: () => setError(null) }
}

/** An online-first action (the workshop): send, wait for the SERVER's verdict, and report it where the user tapped. `run` resolves true
 *  when the command was applied. With no network the command is kept on the device and goes at the next sync: that is `pending`, a third
 *  state beside `ok` and `error` (M23, docs/M23.md item 10: it used to be reported as the red "Pas de réseau" error over a form left open,
 *  although the order existed after the next sync). A caller shows `PendingBanner` and closes its form on `pending`; `run` still resolves
 *  false, so a form that waits for `true` before renewing its command id keeps the same id, and a second tap stays idempotent. */
export function useWorkshopAction() {
  const { t } = useTranslation()
  const { sendAndWait } = useClub()
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [ok, setOk] = useState<string | null>(null)
  const [pending, setPending] = useState(false)
  const run = useCallback(async <K extends CommandKey>(key: K, payload: PayloadOf<K>, successKey: string, opts?: EnqueueOptions): Promise<boolean> => {
    setBusy(true); setError(null); setOk(null); setPending(false)
    try {
      const v = await sendAndWait(key, payload, opts)
      if (v.status === 'queued') { setPending(true); return false }
      if (v.status === 'applied' || v.status === 'applied_with_exception') {
        setOk([t(successKey), ...v.exceptions.map((c) => errorText(t, c))].join(' '))
        return true
      }
      setError(errorText(t, v.code))
      return false
    } catch (e) {
      setError(e instanceof ZodError ? e.issues.map((i) => `${i.path.join('.')}: ${i.message}`).join(' · ') : e instanceof Error && e.message ? e.message : t('errors.unknown'))
      return false
    } finally { setBusy(false) }
  }, [sendAndWait, t])
  return { busy, error, ok, pending, run, reset: () => { setError(null); setOk(null); setPending(false) } }
}
